// Przełącznik języka — JEDNA postać w całym produkcie (MAPA 38, decyzja 5).
//
// Do sesji 38 był rozwijaną listą z flagami: w nawigacji publicznej, w menu
// „Konto”, w Ustawieniach i w powłoce logowania. Artboardy 01a–01d i 09e rysują
// w każdym z tych miejsc segment „PL / EN”, i to jest cała zmiana tutaj —
// zachowanie zostaje co do kroku.
//
// DLACZEGO SEGMENT, A NIE LISTA. Języki są dwa i oba mieszczą się w dwóch
// znakach, więc lista zamieniała jeden ruch w trzy (otwórz, przeczytaj, wybierz)
// i potrzebowała warstwy nad nawigacją. Flagi wyleciały razem z listą: flaga to
// kraj, nie język, a w produkcie z jednym sprzedawcą i dwoma językami niczego nie
// rozstrzygała — nazwa języka zostaje dla czytnika ekranu w `aria-label`.
//
// TRASA WYGRYWA Z PREFERENCJĄ. Na stronach publicznych język jest w adresie
// (`/pl/...`, `/en/...`), więc przełącznik NAWIGUJE do rodzeństwa tej samej
// strony i zachowuje kotwicę; w aplikacji zapisuje wybór (localStorage + PATCH
// `User.locale` przez providera i18n).

import { useI18n } from '../lib/i18n'
import type { Locale } from '../lib/i18n'
import { useLocation, useNavigate } from 'react-router'
import { localeFromPath, siblingLocalePath } from '../lib/publicPaths'
import { Segment } from '../ds/primitives/Segment'
import './LangSwitcher.css'

/** Nazwa języka w NIM SAMYM — to ona trafia do czytnika ekranu. */
const NAMES: Record<Locale, string> = { pl: 'Polski', en: 'English' }
/** Etykieta widoczna: dwa znaki, tyle samo miejsca w obu językach. */
const SHORT: Record<Locale, string> = { pl: 'PL', en: 'EN' }
const ORDER: Locale[] = ['pl', 'en']

/** `variant="segments"` is the same switch drawn for the dark public stage of paczka 37 (`.lang-seg`: header, footer bar,
 *  auth shell); everywhere else it is the DS segment of paczka 38. One component, one navigation rule. */
export default function LangSwitcher({ className = '', compact = false, variant = 'segment', onSelected, onResult }: {
  className?: string
  /** Wersja do nawigacji i menu: 36 px zamiast 44 px. */
  compact?: boolean
  variant?: 'segment' | 'segments'
  /** Wybór padł — menu i arkusz zamykają się na tym, nie na odpowiedzi serwera. */
  onSelected?: () => void
  /**
   * WYNIK zapisu do profilu, gdy już wróci. Ustawienia potwierdzają nim zapis
   * toastem, a nieudany pokazują w wierszu przy przełączniku (artboardy 09g i 07h).
   * „Zapisano.” przed odpowiedzią serwera byłoby zdaniem, którego nikt nie sprawdził.
   */
  onResult?: (ok: boolean, e?: unknown) => void
}) {
  const { locale, setLocale, t } = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const pathLocale = localeFromPath(location.pathname)
  const switchTo = (value: Locale) => {
    if (value === locale) return
    if (pathLocale) navigate(siblingLocalePath(location.pathname, location.hash, value))
    else setLocale(value, onResult)
    onSelected?.()
  }
  if (variant === 'segments') {
    return (
      <div className={`lang-seg ${className}`.trim()} role="group" aria-label={t('lang.switch')}>
        {ORDER.map((value) => (
          <button key={value} type="button" aria-pressed={value === locale}
                  onClick={() => switchTo(value)} title={NAMES[value]}>
            {SHORT[value]}
          </button>
        ))}
      </div>
    )
  }

  return (
    <Segment
      className={`lang-switch${compact ? ' lang-switch-compact' : ''} ${className}`.trim()}
      value={locale}
      onChange={switchTo}
      size={compact ? 'sm' : 'md'}
      aria-label={`${t('lang.switch')}: ${NAMES[locale]}`}
      data-testid="lang-switch"
      options={ORDER.map((value) => ({
        value,
        label: <span aria-label={NAMES[value]}>{SHORT[value]}</span>,
        testId: `lang-${value}`,
      }))}
    />
  )
}
